import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import { PencilIcon } from 'lucide-react';

import Dialog from 'src/components/dialog/Dialog';
import { useUpdateLead } from 'src/services/api/leads/use-update-lead';
import type { LeadFactKey } from 'src/services/api/leads/lead-facts';
import { VOUCHER_TYPE_OPTIONS } from 'src/services/api/leads/lead-facts';

import styles from 'src/components/lead-sidebar/EditFactButton.module.css';

type Props = {
    leadUuid: string;
    /** Modal title, rendered as "Edit {title}". */
    title: string;
    /** Disambiguates this editor's test ids / trigger (e.g. "phone_number", "annual_income"). */
    testId: string;
    /** The lead column being edited — every fact key maps 1:1 to a column, so every
     * edit is a plain lead update (a pending suggestion is auto-resolved server-side). */
    factKey: LeadFactKey;
    value: string;
    /** Optional clickable trigger content; rendered followed by a pencil icon.
     * Defaults to a standalone pencil icon button. */
    children?: React.ReactNode;
};

type IncomeCadence = 'monthly' | 'annual';

/* Fact keys whose lead column is a number; sent as numbers, not strings. */
const INT_KEYS: LeadFactKey[] = ['annual_income', 'household_size'];

/** Pencil button to edit a lead's fact column. Income is edited as a single value
 * with a monthly/annual toggle (stored annually; annual is the default so an
 * unedited save writes the exact stored value back rather than round-tripping it). */
export default function EditFactButton(props: Props) {
    const { leadUuid, title, testId, factKey, children } = props;
    const isIncome = factKey === 'annual_income';
    const isIntKey = INT_KEYS.includes(factKey);
    const isVoucher = factKey === 'voucher_type';

    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState('');
    const [cadence, setCadence] = useState<IncomeCadence>('annual');

    const { mutate: updateLead, isLoading } = useUpdateLead(leadUuid);

    const open = () => {
        setValue(props.value);
        setCadence('annual');
        setIsOpen(true);
    };
    const close = () => setIsOpen(false);

    /* Flipping the cadence converts the number in place, so the shown value keeps
       meaning the same income. */
    const switchCadence = (next: IncomeCadence) => {
        if (next === cadence) {
            return;
        }
        const n = Number(value);
        if (Number.isFinite(n) && value.trim() !== '') {
            setValue(String(next === 'annual' ? n * 12 : Math.round(n / 12)));
        }
        setCadence(next);
    };

    /* A number key must stay a valid positive number — NaN would serialize to null
       and unset the column. */
    const isInvalid = isIntKey && (!Number.isFinite(Number(value)) || Number(value) <= 0);

    const save = () => {
        if (isInvalid) {
            return;
        }
        if (isIntKey) {
            const n = Number(value);
            const annual = isIncome && cadence === 'monthly' ? n * 12 : n;
            if (String(annual) === props.value) {
                close();
                return;
            }
            updateLead({ [factKey]: annual }, { onSuccess: close });
        } else {
            if (value === props.value) {
                close();
                return;
            }
            updateLead({ [factKey]: value }, { onSuccess: close });
        }
    };

    return (
        <>
            {children ? (
                <button
                    type="button"
                    onClick={open}
                    className={styles.textTrigger}
                    aria-label={`Edit ${title}`}
                    data-testid={`edit-fact-${testId}`}
                >
                    {children}
                    <PencilIcon size={12} className={styles.triggerIcon} aria-hidden />
                </button>
            ) : (
                <IconButton
                    size="small"
                    onClick={open}
                    aria-label={`Edit ${title}`}
                    data-testid={`edit-fact-${testId}`}
                    className={styles.editButton}
                >
                    <PencilIcon size={13} />
                </IconButton>
            )}

            <Dialog open={isOpen} onClose={close}>
                <DialogTitle>Edit {title}</DialogTitle>
                <DialogContent className={styles.content}>
                    {isVoucher ? (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            margin="dense"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            data-testid={`edit-fact-input-${testId}`}
                        >
                            {VOUCHER_TYPE_OPTIONS.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    data-testid={`edit-fact-option-${option.value}`}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : (
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            margin="dense"
                            label={isIncome ? (cadence === 'monthly' ? 'Monthly' : 'Annual') : undefined}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            data-testid={`edit-fact-input-${testId}`}
                        />
                    )}
                    {isIncome && (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={cadence}
                            onChange={(_, next: IncomeCadence | null) => next && switchCadence(next)}
                        >
                            <ToggleButton value="monthly" data-testid="edit-fact-cadence-monthly">
                                Monthly
                            </ToggleButton>
                            <ToggleButton value="annual" data-testid="edit-fact-cadence-annual">
                                Annual
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="dark" onClick={close} data-testid={`edit-fact-cancel-${testId}`}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={save}
                        disabled={isLoading || isInvalid}
                        data-testid={`edit-fact-save-${testId}`}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
